import styles from "./DatasetMapping.module.css";
import type { ColumnMapping, ColumnType } from "./types";
import Icon from "@lib/common/components/Icon/Icon";
import { Center, Select, Switch, TextInput, Tooltip } from "@mantine/core";
import classNames from "classnames";
import { useState } from "react";

export default function MappingItem({
  item,
  onUpdate
}: {
  item: ColumnMapping;
  onUpdate: (item: ColumnMapping) => void;
}) {
  const [columnName, setName] = useState(item.column_name);
  const toggleDisabled = () => onUpdate({ ...item, disabled: !item.disabled });
  const changeColumnType = (type: ColumnType) => onUpdate({ ...item, type });
  const changeColumnName = (name: string) => {
    setName(name);
    return onUpdate({ ...item, column_name: name });
  };

  function renderStatus() {
    if (item.valid) {
      return (
        <Icon
          name="circle-check"
          size="1.2rem"
          color={item.disabled ? "var(--mantine-color-disabled-border)" : "green"}
        />
      );
    } else {
      return (
        <Tooltip label={`Column contains no valid datapoints and cannot be used to create a dataset.`} disabled={false}>
          <div>
            <Icon name="circle-exclamation" size="1.2rem" color={"red"} />
          </div>
        </Tooltip>
      );
    }
  }

  return (
    <tr
      className={classNames(styles.mappingTableRow, {
        [styles.mappingTableRowDisabled]: item.valid
      })}
    >
      <td data-center>
        <Center>
          <Tooltip label={"You cannot enable a column that has incomplete datapoints."} disabled={item.valid}>
            <div>
              <Switch checked={!item.disabled && item.valid} disabled={!item.valid} onChange={toggleDisabled} />
            </div>
          </Tooltip>
        </Center>
      </td>
      <td>
        <TextInput
          value={columnName}
          onChange={event => changeColumnName(event.currentTarget.value)}
          disabled={!item.valid || item.isCategorical}
          w={200}
          styles={{
            input: {
              textDecoration: !item.valid ? "line-through" : "none"
            }
          }}
        />
      </td>
      <td style={{ verticalAlign: "middle", textAlign: "center" }}>
        <span style={{ opacity: item.disabled ? 0.2 : 1 }}>
          <Icon name="arrow-right-long" color="gray" />
        </span>
      </td>
      <td>
        <Tooltip
          label={
            "You cannot change the type of this column because at least one of your datapoints contained text or words."
          }
          disabled={!item.isCategorical}
        >
          <div>
            <Select
              data={
                [
                  { value: "parameter", label: "Variable" },
                  { value: "measurement", label: "Result" }
                ] satisfies Array<{ value: ColumnType; label: string }>
              }
              value={item.type}
              placeholder={item.valid ? "" : "Incomplete"}
              disabled={!item.valid || item.isCategorical}
              onChange={value => changeColumnType(value as ColumnType)}
              w={200}
            />
          </div>
        </Tooltip>
      </td>
      <td data-center style={{ lineHeight: 1 }}>
        {renderStatus()}
      </td>
    </tr>
  );
}
